import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded'
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { ActionType, ErrorType } from '../types'
import useAutoTranslate from '../hooks/useAutoTranslate'
import CompactAssetIcon from './CompactAssetIcon'
import { WHEEL_MODE_ICON_BY_KEY } from './gameAssetIcons'

const WHEEL_MODE_OPTIONS = Object.freeze([
    {
        key: 'normal',
        label: 'Lucky Wheel',
        ticketKey: 'LWT',
        fallbackCost: 30
    },
    {
        key: 'affluence',
        label: 'Wheel of Unimaginable Affluence',
        ticketKey: 'SLWT',
        fallbackCost: 1
    }
])

const clampSpinCount = value => {
    const parsed = Number.parseInt(String(value ?? 1), 10)
    if (!Number.isFinite(parsed) || parsed <= 0)
        return 1

    return Math.min(100, parsed)
}

const readNumber = value => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

const formatNumber = value => {
    const parsed = readNumber(value)
    return parsed === undefined ? undefined : new Intl.NumberFormat().format(parsed)
}

const formatTimestamp = value => {
    const parsed = readNumber(value)
    if (parsed === undefined || parsed <= 0)
        return undefined

    return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(new Date(parsed))
}

const getWheelModeOption = modeKey =>
    WHEEL_MODE_OPTIONS.find(option => option.key === modeKey) ?? WHEEL_MODE_OPTIONS[0]

const getModeStatus = (runtimeStatus, modeKey) => {
    const option = getWheelModeOption(modeKey)
    const modeStatus = runtimeStatus?.modes?.[option.key]

    if (modeStatus && typeof modeStatus === 'object' && !Array.isArray(modeStatus))
        return modeStatus

    if (option.key === 'affluence')
        return {
            ticketKey: runtimeStatus?.affluenceTicketKey ?? option.ticketKey,
            ticketCost: runtimeStatus?.affluenceSpinTicketCost ?? option.fallbackCost,
            ticketBalance: runtimeStatus?.affluenceTicketBalance,
            ticketBalanceUpdatedAt: runtimeStatus?.affluenceTicketBalanceUpdatedAt,
            lastSpinResult: runtimeStatus?.lastSpinResult?.modeKey === option.key ? runtimeStatus.lastSpinResult : undefined
        }

    return {
        ticketKey: runtimeStatus?.ticketKey ?? option.ticketKey,
        ticketCost: runtimeStatus?.normalSpinTicketCost ?? option.fallbackCost,
        ticketBalance: runtimeStatus?.ticketBalance,
        ticketBalanceUpdatedAt: runtimeStatus?.ticketBalanceUpdatedAt,
        lastSpinResult: runtimeStatus?.lastSpinResult?.modeKey === option.key || !runtimeStatus?.lastSpinResult?.modeKey ? runtimeStatus?.lastSpinResult : undefined
    }
}

const getTicketBalance = (runtimeStatus, modeKey) => {
    const modeStatus = getModeStatus(runtimeStatus, modeKey)
    const candidates = [
        modeStatus?.ticketBalance,
        modeStatus?.tickets,
        modeStatus?.ticketCount
    ]
    const match = candidates.map(readNumber).find(value => value !== undefined)
    return match === undefined ? undefined : Math.max(0, Math.floor(match))
}

const getTicketCost = (runtimeStatus, modeKey) => {
    const option = getWheelModeOption(modeKey)
    const modeStatus = getModeStatus(runtimeStatus, modeKey)
    return readNumber(modeStatus?.ticketCost) ?? option.fallbackCost
}

const getRunStatusColor = status =>
    status === 'completed' ? 'success' :
        status === 'failed' ? 'error' :
            'default'

function MetricBox({ label, value, caption, icon }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                minHeight: 118,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.025)',
                borderColor: 'rgba(148,163,184,0.18)'
            }}
        >
            <Stack direction="row" spacing={1.2} alignItems="center">
                {icon}
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {label}
                </Typography>
            </Stack>
            <Typography variant="h4" sx={{ mt: 1.1, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                {value}
            </Typography>
            {caption && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.55, color: 'text.secondary' }}>
                    {caption}
                </Typography>
            )}
        </Paper>
    )
}

export default function LuckyWheelSettingsPanel({
    selectedUser,
    runtimeStatus,
    botRunning,
    ws,
    appLanguage = 'en',
    onRequestLiveOps
}) {
    const t = useAutoTranslate(appLanguage, [
        'Lucky Wheel',
        'Wheel of Unimaginable Affluence',
        'Wheel type',
        'Queue spin runs and inspect the latest recorded wheel rewards.',
        'Bot must be running to queue wheel spins.',
        'Current ticket balance',
        'Live from special currency',
        'No ticket data yet',
        'Updated',
        'Spin cost',
        'Tickets',
        'Spin run',
        'Idle',
        'Running',
        'Spins',
        'Start Spins',
        'Not enough tickets for the requested spins.',
        'Spin request sent.',
        'Latest Spin Result',
        'No completed wheel spin yet.',
        'Reward',
        'Category',
        'Class',
        'Progress',
        'Recent Spin Runs',
        'No spin runs yet.',
        'completed',
        'failed',
        'Requested',
        'Completed',
        'Ticket balance after',
        'Last error'
    ])
    const [spinInput, setSpinInput] = React.useState('1')
    const [wheelMode, setWheelMode] = React.useState('normal')
    const [feedback, setFeedback] = React.useState(null)

    const selectedMode = getWheelModeOption(wheelMode)
    const modeStatus = getModeStatus(runtimeStatus, wheelMode)
    const spinCount = clampSpinCount(spinInput)
    const ticketBalance = getTicketBalance(runtimeStatus, wheelMode)
    const ticketBalanceText = formatNumber(ticketBalance) ?? t('No ticket data yet')
    const ticketUpdatedAt = formatTimestamp(modeStatus?.ticketBalanceUpdatedAt)
    const ticketKey = modeStatus?.ticketKey ?? selectedMode.ticketKey
    const spinCost = getTicketCost(runtimeStatus, wheelMode)
    const estimatedCost = spinCount * spinCost
    const busy = Boolean(runtimeStatus?.busy)
    const insufficientTickets = ticketBalance !== undefined && ticketBalance < estimatedCost
    const latestSpin = modeStatus?.lastSpinResult
    const recentRuns = (Array.isArray(runtimeStatus?.recentRuns) ? runtimeStatus.recentRuns : [])
        .filter(run => !run?.modeKey || run.modeKey === wheelMode)

    const handleStart = () => {
        if (!ws || !selectedUser?.id || !botRunning || busy || insufficientTickets)
            return

        const spins = clampSpinCount(spinInput)
        ws.send(JSON.stringify([
            ErrorType.Success,
            ActionType.LuckyWheelSpin,
            { ...selectedUser, spins, wheelMode }
        ]))
        setFeedback({ severity: 'success', message: t('Spin request sent.') })
        onRequestLiveOps?.()
    }

    return (
        <Stack spacing={2.4}>
            <Box>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <CasinoRoundedIcon sx={{ color: '#f0abfc' }} />
                    <Typography variant="h5" sx={{ fontSize: '1.2rem' }}>
                        {t('Lucky Wheel')}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.7, color: 'text.secondary' }}>
                    {t('Queue spin runs and inspect the latest recorded wheel rewards.')}
                </Typography>
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    borderColor: 'rgba(148,163,184,0.18)'
                }}
            >
                <Typography variant="caption" sx={{ display: 'block', px: 1, pb: 0.8, color: 'text.secondary', fontWeight: 800 }}>
                    {t('Wheel type')}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={wheelMode}
                    onChange={(_, nextMode) => {
                        if (nextMode)
                            setWheelMode(nextMode)
                    }}
                    sx={{
                        gap: 1,
                        '& .MuiToggleButton-root': {
                            border: '1px solid rgba(148,163,184,0.18)',
                            borderRadius: '8px !important',
                            color: 'text.secondary',
                            py: 1.2,
                            textTransform: 'none',
                            fontWeight: 800
                        },
                        '& .Mui-selected': {
                            color: '#f8fafc',
                            backgroundColor: 'rgba(143,211,255,0.14)'
                        }
                    }}
                >
                    {WHEEL_MODE_OPTIONS.map(option => (
                        <ToggleButton key={option.key} value={option.key}>
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    minWidth: 0
                                }}
                            >
                                <CompactAssetIcon iconUrl={WHEEL_MODE_ICON_BY_KEY[option.key]} itemType="building" size={30} />
                                <Box
                                    component="span"
                                    sx={{
                                        minWidth: 0,
                                        whiteSpace: { xs: 'normal', md: 'nowrap' },
                                        overflowWrap: 'anywhere'
                                    }}
                                >
                                    {t(option.label)}
                                </Box>
                            </Box>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Paper>

            {!botRunning && (
                <Alert severity="warning">
                    {t('Bot must be running to queue wheel spins.')}
                </Alert>
            )}
            {insufficientTickets && (
                <Alert severity="error">
                    {t('Not enough tickets for the requested spins.')}
                </Alert>
            )}
            {feedback && (
                <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                    {feedback.message}
                </Alert>
            )}

            <Grid container spacing={1.8}>
                <Grid item xs={12} md={4}>
                    <MetricBox
                        label={t('Current ticket balance')}
                        value={ticketBalanceText}
                        caption={ticketUpdatedAt ? `${t('Updated')}: ${ticketUpdatedAt}` : `${t('Live from special currency')} ${ticketKey}`}
                        icon={<ConfirmationNumberRoundedIcon fontSize="small" sx={{ color: '#facc15' }} />}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <MetricBox
                        label={t('Spin cost')}
                        value={`${formatNumber(spinCost) ?? spinCost} ${t('Tickets')}`}
                        caption={`${t('Requested')}: ${formatNumber(estimatedCost) ?? estimatedCost} ${ticketKey}`}
                        icon={<CasinoRoundedIcon fontSize="small" sx={{ color: '#8fd3ff' }} />}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <MetricBox
                        label={t('Spin run')}
                        value={busy ? t('Running') : t('Idle')}
                        caption={busy ? `${runtimeStatus?.activeModeLabel ?? ''} ${runtimeStatus?.completedSpins ?? 0}/${runtimeStatus?.requestedSpins ?? spinCount}`.trim() : undefined}
                        icon={<AutorenewRoundedIcon fontSize="small" sx={{ color: busy ? '#22c55e' : 'text.secondary' }} />}
                    />
                </Grid>
            </Grid>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    borderColor: 'rgba(148,163,184,0.18)'
                }}
            >
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label={t('Spins')}
                            value={spinInput}
                            onChange={event => setSpinInput(event.target.value)}
                            inputProps={{ min: 1, max: 100, step: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayArrowRoundedIcon />}
                            disabled={!botRunning || busy || insufficientTickets || !selectedUser?.id}
                            onClick={handleStart}
                            sx={{ py: 1.6, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                        >
                            {t('Start Spins')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    borderColor: 'rgba(148,163,184,0.18)'
                }}
            >
                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                    {t('Latest Spin Result')}
                </Typography>
                {latestSpin ? (
                    <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                            <Chip label={`${t('Reward')}: ${latestSpin.rewardSummary ?? 'OK'}`} />
                            {latestSpin.winningCategory !== undefined && (
                                <Chip label={`${t('Category')}: ${latestSpin.winningCategory}`} variant="outlined" />
                            )}
                            {latestSpin.modeLabel && (
                                <Chip label={latestSpin.modeLabel} variant="outlined" />
                            )}
                            {latestSpin.currentWinClass !== undefined && (
                                <Chip label={`${t('Class')}: ${latestSpin.currentWinClass}`} variant="outlined" />
                            )}
                            {latestSpin.winClassProgress !== undefined && (
                                <Chip label={`${t('Progress')}: ${latestSpin.winClassProgress}`} variant="outlined" />
                            )}
                        </Stack>
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ mt: 1.2, color: 'text.secondary' }}>
                        {t('No completed wheel spin yet.')}
                    </Typography>
                )}
            </Paper>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    borderColor: 'rgba(148,163,184,0.18)'
                }}
            >
                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                    {t('Recent Spin Runs')}
                </Typography>
                {recentRuns.length > 0 ? (
                    <Stack divider={<Divider flexItem />} spacing={1.3} sx={{ mt: 1.3 }}>
                        {recentRuns.slice(0, 6).map(run => (
                            <Stack key={run.id ?? `${run.startedAt}-${run.finishedAt}`} spacing={0.8}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            size="small"
                                            color={getRunStatusColor(run.status)}
                                            label={t(run.status ?? 'completed')}
                                        />
                                        {run.modeLabel && (
                                            <Chip size="small" variant="outlined" label={run.modeLabel} />
                                        )}
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {formatTimestamp(run.finishedAt) ?? formatTimestamp(run.startedAt) ?? ''}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                        {`${run.completedSpins ?? 0}/${run.requestedSpins ?? 0}`}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {`${t('Ticket balance after')}: ${formatNumber(run.ticketBalanceAfter) ?? t('No ticket data yet')} ${run.ticketKey ?? ticketKey}`}
                                </Typography>
                                {run.lastError && (
                                    <Typography variant="caption" sx={{ color: 'error.light' }}>
                                        {`${t('Last error')}: ${run.lastError}`}
                                    </Typography>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ mt: 1.2, color: 'text.secondary' }}>
                        {t('No spin runs yet.')}
                    </Typography>
                )}
            </Paper>
        </Stack>
    )
}
